import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ChevronDown, ChevronUp, User, Heart, MapPin, FileText, Edit } from "lucide-react";
import { hasPermission } from "../utils/permissions";

const EditPatient = () => {
  const { patient_id } = useParams();
  const navigate = useNavigate();

  const getToken = () => localStorage.getItem('session_token') || '';
  const authHeaders = () => {
    const t = getToken();
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  const [formData, setFormData] = useState({
    patient_date: "",            // ✅ NEW
    name: "",
    mobile: "",
    mobile2: "",
    mobile3: "",
    whatsapp: "",
    email: "",
    age: "",
    sex: "M",
    weight: "",
    height: "",
    bp: 0,
    diabetes: 0,
    thyroid: 0,
    address: "",
    pincode: "",
    city: "",
    state: "",
    country: "India",
    service_type: "VISIT",
  });

  const [loading, setLoading] = useState(true);

  // Section toggle states
  const [isPatientInfoOpen, setIsPatientInfoOpen] = useState(true);
  const [isMedicalHistoryOpen, setIsMedicalHistoryOpen] = useState(true);
  const [isAddressInfoOpen, setIsAddressInfoOpen] = useState(true);
  const [isServiceModeOpen, setIsServiceModeOpen] = useState(true);

  // Fetch patient data
  useEffect(() => {
    const fetchPatient = async () => {
      const canEdit = await hasPermission('operation', 'editpatiententry');
      if (!canEdit) {
        alert('You do not have permission to edit patient entries.');
        navigate('/PatientList');
        return;
      }

      try {
        const res = await fetch(
          `https://newerp.awasthivitality.com/api/patient/edit/${patient_id}`,
          { headers: authHeaders() }
        );
        const result = await res.json().catch(() => ({}));

        if (result.success && result.data) {
          const data = result.data;
          setFormData({
            patient_date: data.patient_date || data.created_at?.split("T")[0] || "", // ✅ NEW

            name: data.name || "",
            mobile: data.mobile || "",
            mobile2: data.mobile2 || data.whatsapp || "",
            mobile3: data.mobile3 || "",
            whatsapp: data.whatsapp || data.mobile2 || "",
            email: data.email || "",
            age: data.age || "",
            sex: data.sex || "M",
            weight: data.weight || "",
            height: data.height || "",
            bp: data.bp === 'Yes' || data.bp === 1 || data.bp === '1' ? 1 : 0,
            diabetes: data.diabetes === 'Yes' || data.diabetes === 1 || data.diabetes === '1' ? 1 : 0,
            thyroid: data.thyroid === 'Yes' || data.thyroid === 1 || data.thyroid === '1' ? 1 : 0,
            address: data.address || "",
            pincode: data.pincode || "",
            city: data.city || "",
            state: data.state || "",
            country: data.country || "India",
            service_type: data.service_type || "VISIT",
            patient_pic: data.patient_pic || ""

          });
        } else if (res.status === 403) {
          alert(`${result.message || 'Access denied.'}${result.required_permission ? `\nRequired: ${result.required_permission}` : ''}`);
          navigate('/PatientList');
        } else if (res.status === 401) {
          alert(result.message || 'Unauthorized - please login again.');
          navigate('/PatientList');
        } else {
          alert(result.message || "Failed to fetch patient details");
          navigate('/PatientList');
        }
      } catch (err) {
        console.error("Error fetching patient:", err);
        alert("Error fetching patient data");
      } finally {
        setLoading(false);
      }
    };
    fetchPatient();
  }, [patient_id]);

  // Handle input change
  const handleChange = (e) => {
    const { name, value, type } = e.target;

    if (type === "radio") {
      if (name === "bp" || name === "diabetes" || name === "thyroid") {
        setFormData((prev) => ({ ...prev, [name]: parseInt(value) }));
      } else {
        setFormData((prev) => ({ ...prev, [name]: value }));
      }
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }

  };
  const handleChangeProfile = (e) => {
    const file = e.target.files[0];

    if (!file) return;

    setFormData((prev) => ({
      ...prev,
      patient_pic: file
    }));
  };
  // Submit update
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Prepare data - map mobile2 to whatsapp if needed, and convert boolean fields
      const updateData = {
        ...formData,
        patient_date: formData.patient_date || new Date().toISOString().split("T")[0],
        mobile2: formData.mobile2 || formData.whatsapp || "",
        bp: formData.bp === 1 ? "Yes" : "No",
        diabetes: formData.diabetes === 1 ? "Yes" : "No",
        thyroid: formData.thyroid === 1 ? "Yes" : "No",
      };
      const form = new FormData();

      form.append("_method", "PUT"); // IMPORTANT

      Object.keys(updateData).forEach((key) => {
        if (key !== "patient_pic") {
          form.append(key, updateData[key]);
        }
      });

      if (formData.patient_pic instanceof File) {
        form.append("patient_pic", formData.patient_pic);
      }
      const res = await fetch(
        `https://newerp.awasthivitality.com/api/patient/update/${patient_id}`,
        {
          method: "POST",   // use POST for file upload
          headers: {
            ...authHeaders()
          },
          body: form
        }
      );

      const result = await res.json().catch(() => ({}));

     if (res.ok && result.success) {
  alert(result.msg || "Patient updated successfully");

  navigate(`/patient/details/${patient_id}`, { replace: true });
} else {
        if (res.status === 422) {
          let errorMessage = result.message || 'Validation failed:\n';
          if (result.errors) {
            const errorList = Object.entries(result.errors)
              .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
              .join('\n');
            errorMessage += errorList;
          }
          alert(errorMessage);
        } else if (res.status === 403) {
          alert(`${result.message || 'Access denied.'}${result.required_permission ? `\nRequired: ${result.required_permission}` : ''}`);
        } else if (res.status === 401) {
          alert(result.message || 'Unauthorized - please login again.');
        } else {
          alert(result.msg || result.message || "Update failed");
        }
      }
    } catch (err) {
      console.error("Update error:", err);
      alert("Something went wrong while updating. Check console for details.");
    }
  };

  if (loading) return <div style={{ padding: "2rem", textAlign: "center" }}>Loading patient data...</div>;

  return (
    <div className="app-container">
      <style>{`
        :root{
          --primary-bg-color: #f6f8fb;
          --main-card-bg-color: #ffffff;
          --main-card-border-color: #e6e9ef;
          --main-card-shadow: 0 16px 30px rgba(15,23,42,0.06);
          --header-border-color: #f1f4f3;
          --header-title-color: #0f172a;
          --text-color: #374151;
          --accent-color: #06b6d4;
          --button-gradient-start: #38bdf8;
          --button-gradient-end: #2dd4bf;
          --button-hover-gradient-start: #0ea5e9;
          --button-hover-gradient-end: #14b8a6;
          --section-gradient-start: #06b6d4;
          --section-gradient-end: #f1f4f3;
          --submit-button-gradient-start: #fb923c;
          --submit-button-gradient-end: #f59e0b;
          --submit-button-hover-gradient-start: #f97316;
          --submit-button-hover-gradient-end: #eab308;
          --input-border-color: #d1d5db;
          --input-bg-color: #ffffff;
          --input-muted-bg: #f8fafc;
          --input-focus-color: rgba(6,182,212,0.18);
          --error-color: #ef4444;
          --success-color: #10b981;
          --warning-color: #f59e0b;
          --info-color: #3b82f6;
          --radius-sm: 6px;
          --radius-md: 12px;
        }

        *{box-sizing: border-box}
        body { font-family: Inter, ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial; margin:0; padding:0; color:var(--text-color); }

        .app-container {
          min-height: 100vh;
          background: linear-gradient(135deg, #f6f8fb 0%, #eef2f7 50%, #f6f8fb 100%);
          background-size: 200% 200%;
          display: flex;
          justify-content: center;
          align-items: flex-start;
          padding: 1rem;
        }

        @media (min-width: 640px) {
          .app-container { padding: 1.5rem; }
        }

        .main-card {
          width: 100%;
          max-width: 100%;
          margin: 0 auto;
          background: var(--main-card-bg-color);
          border-radius: var(--radius-md);
          box-shadow: var(--main-card-shadow);
          border: 1px solid var(--main-card-border-color);
          overflow: hidden;
        }

        .header {
          display: flex;
          flex-direction: column;
          gap: 0.15rem;
          padding: 0.5rem 1rem;
          border-bottom: 2px solid var(--header-border-color);
          background: linear-gradient(180deg, rgba(255,255,255,0.7), rgba(255,255,255,0.95));
        }

        @media (min-width: 640px) { 
          .header { flex-direction: row; align-items: center; justify-content: space-between; }
        }

        .header-title { display:flex; align-items:center; gap:0.5rem; font-size:1.25rem; font-weight:800; color:var(--header-title-color); }
        .header-title .icon { color: var(--accent-color); }

        .main-content { padding: 1.2rem; }
        form { width:100%; }

        .form-section-container { margin-bottom:1.25rem; }
        .form-section-header {
          display:flex;
          justify-content:space-between;
          align-items:center;
          gap:1rem;
          padding:0.95rem 1rem;
          border-radius: 10px 10px 0 0;
          background-image: linear-gradient(90deg, var(--section-gradient-start), var(--section-gradient-end));
          color: white;
          cursor: pointer;
          box-shadow: 0 8px 20px rgba(3,105,161,0.06);
          transition: transform .14s ease, box-shadow .14s ease;
        }
        .form-section-header:hover { transform: translateY(-3px); }
        .header-content { display:flex; align-items:center; gap:0.6rem; }
        .section-title { font-weight:600; font-size:1rem; margin:0; }

        .form-section-body {
          padding:0.25rem;
          border:1px solid var(--main-card-border-color);
          border-top: none;
          background: var(--main-card-bg-color);
          border-radius: 0 0 10px 10px;
        }

        .form-section {
          background: linear-gradient(145deg, rgba(246, 248, 251, 0.5), rgba(255, 255, 255, 0.95));
          border-radius: 12px;
          padding: 1.5rem;
          margin-bottom: 1.5rem;
          border: 1px solid rgba(230, 233, 239, 0.8);
          box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
        }

        .form-section-title {
          font-size: 1.5rem;
          font-weight: 900;
          color: var(--accent-color);
          margin: 0 0 1.5rem 0;
          text-transform: uppercase;
          letter-spacing: 1px;
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 1rem 1.5rem;
          background: linear-gradient(135deg, rgba(6, 182, 212, 0.15) 0%, rgba(8, 145, 178, 0.1) 100%);
          border-left: 5px solid var(--accent-color);
          border-radius: 8px;
          box-shadow: 0 4px 16px rgba(6, 182, 212, 0.15);
          text-align: left;
        }

        .form-section-title .section-icon {
          width: 28px;
          height: 28px;
          color: var(--accent-color);
        }

        .form-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 1rem;
          align-items: flex-start;
        }

        .form-grid > div {
          display: flex;
          flex-direction: column;
          min-width: 0;
        }

        .form-field-label { display:block; margin-bottom:0.35rem; font-weight:600; color:var(--text-color); font-size:0.92rem; }
        .form-field-input,
        .form-field-select,
        .form-field-textarea {
          width:100%;
          padding:0.55rem;
          border-radius:8px;
          border:1px solid var(--input-border-color);
          background:var(--input-bg-color);
          transition: box-shadow .16s ease, border-color .16s ease;
          font-size:0.95rem;
        }
        .form-field-select { background: #fff; }
        .form-field-input:focus,
        .form-field-select:focus,
        .form-field-textarea:focus {
          outline:none;
          border-color: var(--accent-color);
          box-shadow: 0 0 0 5px var(--input-focus-color);
        }
        .required-star { color: var(--error-color); margin-left:4px; font-weight:700; }

        .horizontal-form {
          display: flex;
          flex-wrap: wrap;
          gap: 25rem;
          align-items: flex-start;
        }

        .form-item {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .radio-field-group { display:flex; flex-wrap:wrap; gap:1rem; margin-top:0.35rem; }
        .radio-field-label { display:flex; align-items:center; gap:0.55rem; cursor:pointer; }
        .radio-input { width:18px; height:18px; accent-color: var(--accent-color); }

        .info-note { font-size:0.8rem; color:#6b7280; margin-top:0.35rem; }

        .submit-button-container {
          display: flex;
          justify-content: center;
          // margin-top: 2rem;
        }

        .submit-button {
          background: linear-gradient(135deg, var(--submit-button-gradient-start) 0%, var(--submit-button-gradient-end) 100%);
          color: white;
          border: none;
          padding: 1rem 3rem;
          border-radius: 12px;
          font-size: 1rem;
          font-weight: 700;
          cursor: pointer;
          box-shadow: 0 4px 16px rgba(6, 182, 212, 0.3);
          transition: all 0.3s ease;
          min-width: 220px;
        }

        .submit-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 24px rgba(6, 182, 212, 0.4);
          background: linear-gradient(135deg, var(--submit-button-hover-gradient-start) 0%, var(--submit-button-hover-gradient-end) 100%);
        }

        .service-mode-options {
          display: flex;
          flex-wrap: wrap;
          gap: 1rem;
          margin-top: 0.5rem;
        }

        .service-mode-option {
          display: flex;
          align-items: center;
          gap: 7.5rem;
          padding: 0.5rem 1rem;
          border-radius: 8px;
          background: var(--input-muted-bg);
          border: 1px solid var(--input-border-color);
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .service-mode-option:hover {
          background: var(--input-focus-color);
          border-color: var(--accent-color);
        }

        .service-mode-option input[type="radio"] {
          margin: 0;
          accent-color: var(--accent-color);
        }

        @media (max-width: 992px) {
          .form-grid {
            grid-template-columns: repeat(2, 1fr);
          }
          .form-grid > div[style*="grid-column: span 2"] {
            grid-column: span 2;
          }
        }

        @media (max-width: 600px) {
          .form-grid {
            grid-template-columns: 1fr;
          }
          .form-grid > div[style*="grid-column: span 2"] {
            grid-column: span 1;
          }
        }

        .error-message {
          color: var(--error-color);
          font-size: 0.85rem;
          margin-top: 0.25rem;
        }
          .form-eight-columns {
  display: grid;
  grid-template-columns: repeat(8, 1fr); /* 8 equal columns */
  gap: 16px; /* spacing between columns */
  align-items: end; /* aligns inputs neatly */
}

.form-eight-columns > div {
  display: flex;
  flex-direction: column;
}.form-address-inline {
  display: flex;
  flex-wrap: nowrap; /* All in one line */
  gap: 16px; /* spacing between fields */
  overflow-x: auto; /* horizontal scroll if screen too small */
}

.form-address-inline > div {
  flex: 0 0 auto;
  min-width: 220px; /* each field gets a fixed width */
}
/* ================= MOBILE RESPONSIVE ONLY ================= */
@media (max-width: 768px) {

  /* Card spacing */
  .main-card {
    border-radius: 10px;
  }

  .main-content {
    padding: 0.75rem;
  }

  /* Section headers */
  .form-section-header {
    padding: 0.75rem;
  }

  .section-title {
    font-size: 0.95rem;
  }

  /* Patient info: 8 columns → 1 column */
  .form-eight-columns {
    grid-template-columns: 1fr !important;
    gap: 0.75rem;
  }

  /* Normal grid also single column */
  .form-grid {
    grid-template-columns: 1fr !important;
  }

  /* Remove forced span on mobile */
  .form-grid > div[style*="grid-column"] {
    grid-column: span 1 !important;
  }

  /* Medical history radio layout */
  .horizontal-form {
    flex-direction: column;
    gap: 1rem; /* remove 25rem gap */
  }

  /* Radio buttons stack nicely */
  .radio-field-group {
    gap: 0.75rem;
  }

  /* Address section: disable horizontal scroll */
  .form-address-inline {
    flex-wrap: wrap;
    overflow-x: hidden;
  }

  .form-address-inline > div {
    min-width: 100%;
  }

  /* Service mode buttons stack */
  .service-mode-options {
    flex-direction: column;
    gap: 0.5rem;
  }

  .service-mode-option {
    justify-content: space-between;
    gap: 0.5rem; /* remove 7.5rem gap */
    padding: 0.6rem 0.75rem;
    // display:flex;
    FLEX-DIRECTION: column;
    align-items:flex-start !important;
  }

  /* Inputs & selects */
  .form-field-input,
  .form-field-select {
    font-size: 0.9rem;
    padding: 0.5rem;
  }

  /* Submit button */
  .submit-button {
    width: 100%;
    padding: 0.9rem;
    font-size: 0.95rem;
  }
}
/* ================= END MOBILE FIX ================= */

      `}</style>

      <div className="main-card">
        <header className="header">
          <h1 className="header-title">
            <Edit className="icon" />
            Edit Patient
          </h1>
        </header>

        <main className="main-content">
          <form onSubmit={handleSubmit}>
            {/* Patient Information Section */}
            <div className="form-section-container">
              <div className="form-section-header" onClick={() => setIsPatientInfoOpen(!isPatientInfoOpen)} role="button" tabIndex={0}>
                <div className="header-content">
                  <User size={20} />
                  <h2 className="section-title">Patient Information</h2>
                </div>
                {isPatientInfoOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </div>
              {isPatientInfoOpen && (
                <div className="form-section-body">
                  <div className="form-grid form-eight-columns">
                    <div>
                      <label className="form-field-label">
                        Date <span className="required-star">*</span>
                      </label>
                      <input
                        type="date"
                        name="patient_date"
                        value={formData.patient_date || ""}
                        onChange={handleChange}
                        className="form-field-input"
                      />
                    </div>

                    <div>
                      <label className="form-field-label">Patient Name <span className="required-star">*</span></label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name || ""}
                        onChange={handleChange}
                        placeholder="Enter Name"
                        className="form-field-input"
                      />
                    </div>

                    <div>
                      <label className="form-field-label">Mobile <span className="required-star">*</span></label>
                      <input
                        type="text"
                        name="mobile"
                        value={formData.mobile || ""}
                        onChange={handleChange}
                        placeholder="Enter Mobile"
                        className="form-field-input"
                        maxLength="10"
                      />
                    </div>

                    <div>
                      <label className="form-field-label">Whatsapp Number</label>
                      <input
                        type="text"
                        name="mobile2"
                        value={formData.mobile2 || formData.whatsapp || ""}
                        onChange={handleChange}
                        placeholder="Enter Whatsapp"
                        className="form-field-input"
                        maxLength="10"
                      />
                    </div>

                    {/* <div>
                      <label className="form-field-label">Mobile 3</label>
                      <input
                        type="text"
                        name="mobile3"
                        value={formData.mobile3 || ""}
                        onChange={handleChange}
                        placeholder="Enter Mobile 3"
                        className="form-field-input"
                        maxLength="10"
                      />
                    </div> */}

                    {/* <div>
                      <label className="form-field-label">Email Id</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email || ""}
                        onChange={handleChange}
                        placeholder="Enter Email Id"
                        className="form-field-input"
                      />
                    </div> */}

                    <div>
                      <label className="form-field-label">Age <span className="required-star">*</span></label>
                      <input
                        type="number"
                        name="age"
                        value={formData.age || ""}
                        onChange={handleChange}
                        placeholder="Enter Age"
                        className="form-field-input"
                        min="0"
                        max="120"
                      />
                    </div>

                    <div>
                      <label className="form-field-label">Sex</label>
                      <select
                        name="sex"
                        value={formData.sex || "M"}
                        onChange={handleChange}
                        className="form-field-select"
                      >
                        <option value="M">Male</option>
                        <option value="F">Female</option>
                        <option value="O">Other</option>
                      </select>
                    </div>

                    <div>
                      <label className="form-field-label">Weight</label>
                      <input
                        type="text"
                        name="weight"
                        value={formData.weight || ""}
                        onChange={handleChange}
                        placeholder="Enter Weight"
                        className="form-field-input"
                      />
                    </div>

                    <div>
                      <label className="form-field-label">Height</label>
                      <input
                        type="text"
                        name="height"
                        value={formData.height || ""}
                        onChange={handleChange}
                        placeholder="Enter Height"
                        className="form-field-input"
                      />
                    </div>
                    <div>
                      <label className="form-field-label">Profile Photo</label>

                      <input
                        type="file"
                        name="patient_pic"
                        accept="image/*"
                        onChange={handleChangeProfile}
                      />
                      {formData.patient_pic && (
                        <div style={{ marginTop: "10px" }}>
                          <img
                            src={
                              typeof formData.patient_pic === "string"
                                ? formData.patient_pic
                                : URL.createObjectURL(formData.patient_pic)
                            }
                            alt="Preview"
                            style={{
                              width: "80px",
                              height: "80px",
                              borderRadius: "6px",
                              objectFit: "cover",
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Medical History Section */}
            <div className="form-section-container">
              <div className="form-section-header" onClick={() => setIsMedicalHistoryOpen(!isMedicalHistoryOpen)} role="button" tabIndex={0}>
                <div className="header-content">
                  <Heart size={20} />
                  <h2 className="section-title">Medical History</h2>
                </div>
                {isMedicalHistoryOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </div>
              {isMedicalHistoryOpen && (
                <div className="form-section-body">
                  <div className="horizontal-form">
                    <div className="form-item">
                      <label className="form-field-label">
                        BP <span className="required-star">*</span>
                      </label>
                      <div className="radio-field-group">
                        <label className="radio-field-label">
                          <input
                            type="radio"
                            name="bp"
                            value={1}
                            checked={formData.bp === 1}
                            onChange={handleChange}
                            className="radio-input"
                          />
                          <span>Yes</span>
                        </label>
                        <label className="radio-field-label">
                          <input
                            type="radio"
                            name="bp"
                            value={0}
                            checked={formData.bp === 0}
                            onChange={handleChange}
                            className="radio-input"
                          />
                          <span>No</span>
                        </label>
                      </div>
                    </div>

                    <div className="form-item">
                      <label className="form-field-label">
                        Diabetes <span className="required-star">*</span>
                      </label>
                      <div className="radio-field-group">
                        <label className="radio-field-label">
                          <input
                            type="radio"
                            name="diabetes"
                            value={1}
                            checked={formData.diabetes === 1}
                            onChange={handleChange}
                            className="radio-input"
                          />
                          <span>Yes</span>
                        </label>
                        <label className="radio-field-label">
                          <input
                            type="radio"
                            name="diabetes"
                            value={0}
                            checked={formData.diabetes === 0}
                            onChange={handleChange}
                            className="radio-input"
                          />
                          <span>No</span>
                        </label>
                      </div>
                    </div>

                    <div className="form-item">
                      <label className="form-field-label">
                        Thyroid <span className="required-star">*</span>
                      </label>
                      <div className="radio-field-group">
                        <label className="radio-field-label">
                          <input
                            type="radio"
                            name="thyroid"
                            value={1}
                            checked={formData.thyroid === 1}
                            onChange={handleChange}
                            className="radio-input"
                          />
                          <span>Yes</span>
                        </label>
                        <label className="radio-field-label">
                          <input
                            type="radio"
                            name="thyroid"
                            value={0}
                            checked={formData.thyroid === 0}
                            onChange={handleChange}
                            className="radio-input"
                          />
                          <span>No</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Address Information Section */}
            <div className="form-section-container">
              <div className="form-section-header" onClick={() => setIsAddressInfoOpen(!isAddressInfoOpen)} role="button" tabIndex={0}>
                <div className="header-content">
                  <MapPin size={20} />
                  <h2 className="section-title">Address Information</h2>
                </div>
                {isAddressInfoOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </div>
              {isAddressInfoOpen && (
                <div className="form-section-body">
                  <div className="form-grid form-address-inline">
                    <div style={{ gridColumn: "span 2" }}>
                      <label className="form-field-label">Address</label>
                      <input
                        type="text"
                        name="address"
                        value={formData.address || ""}
                        onChange={handleChange}
                        placeholder="Enter Patient Address"
                        className="form-field-input"
                      />
                    </div>

                    <div>
                      <label className="form-field-label">Pincode</label>
                      <input
                        type="text"
                        name="pincode"
                        value={formData.pincode || ""}
                        onChange={handleChange}
                        placeholder="Enter Pincode"
                        className="form-field-input"
                        maxLength="6"
                        pattern="[0-9]{6}"
                      />
                    </div>

                    <div>
                      <label className="form-field-label">City <span className="required-star">*</span></label>
                      <input
                        type="text"
                        name="city"
                        value={formData.city || ""}
                        onChange={handleChange}
                        placeholder="Enter Patient City"
                        className="form-field-input"
                      />
                    </div>

                    <div>
                      <label className="form-field-label">State <span className="required-star">*</span></label>
                      <select
                        name="state"
                        value={formData.state || ""}
                        onChange={handleChange}
                        className="form-field-select"
                      >
                        <option value="">Select</option>
                        <option value="Andhra Pradesh">Andhra Pradesh</option>
                        <option value="Arunachal Pradesh">Arunachal Pradesh</option>
                        <option value="Assam">Assam</option>
                        <option value="Bihar">Bihar</option>
                        <option value="Chandigarh">Chandigarh</option>
                        <option value="Chhattisgarh">Chhattisgarh</option>
                        <option value="Delhi">Delhi</option>
                        <option value="Goa">Goa</option>
                        <option value="Gujarat">Gujarat</option>
                        <option value="Haryana">Haryana</option>
                        <option value="Himachal Pradesh">Himachal Pradesh</option>
                        <option value="Jammu & Kashmir">Jammu & Kashmir</option>
                        <option value="Jharkhand">Jharkhand</option>
                        <option value="Karnataka">Karnataka</option>
                        <option value="Kerala">Kerala</option>
                        <option value="Lakshadweep">Lakshadweep</option>
                        <option value="Madhya Pradesh">Madhya Pradesh</option>
                        <option value="Maharashtra">Maharashtra</option>
                        <option value="Manipur">Manipur</option>
                        <option value="Meghalaya">Meghalaya</option>
                        <option value="Mizoram">Mizoram</option>
                        <option value="Nagaland">Nagaland</option>
                        <option value="Odisha">Odisha</option>
                        <option value="Puducherry">Puducherry</option>
                        <option value="Punjab">Punjab</option>
                        <option value="Rajasthan">Rajasthan</option>
                        <option value="Sikkim">Sikkim</option>
                        <option value="Tamil Nadu">Tamil Nadu</option>
                        <option value="Telangana">Telangana</option>
                        <option value="Tripura">Tripura</option>
                        <option value="Uttar Pradesh">Uttar Pradesh</option>
                        <option value="Uttarakhand">Uttarakhand</option>
                        <option value="West Bengal">West Bengal</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>

                    <div>
                      <label className="form-field-label">Country <span className="required-star">*</span></label>
                      <select
                        name="country"
                        value={formData.country || "India"}
                        onChange={handleChange}
                        className="form-field-select"
                      >
                        <option value="India">India</option>
                        <option value="Afghanistan">Afghanistan</option>
                        <option value="Albania">Albania</option>
                        <option value="Algeria">Algeria</option>
                        <option value="Argentina">Argentina</option>
                        <option value="Australia">Australia</option>
                        <option value="Austria">Austria</option>
                        <option value="Bahrain">Bahrain</option>
                        <option value="Bangladesh">Bangladesh</option>
                        <option value="Belgium">Belgium</option>
                        <option value="Brazil">Brazil</option>
                        <option value="Canada">Canada</option>
                        <option value="China">China</option>
                        <option value="Denmark">Denmark</option>
                        <option value="Egypt">Egypt</option>
                        <option value="Finland">Finland</option>
                        <option value="France">France</option>
                        <option value="Germany">Germany</option>
                        <option value="Greece">Greece</option>
                        <option value="Hong Kong">Hong Kong</option>
                        <option value="Indonesia">Indonesia</option>
                        <option value="Iran">Iran</option>
                        <option value="Iraq">Iraq</option>
                        <option value="Ireland">Ireland</option>
                        <option value="Israel">Israel</option>
                        <option value="Italy">Italy</option>
                        <option value="Japan">Japan</option>
                        <option value="Jordan">Jordan</option>
                        <option value="Kenya">Kenya</option>
                        <option value="Kuwait">Kuwait</option>
                        <option value="Malaysia">Malaysia</option>
                        <option value="Maldives">Maldives</option>
                        <option value="Mexico">Mexico</option>
                        <option value="Nepal">Nepal</option>
                        <option value="Netherlands">Netherlands</option>
                        <option value="New Zealand">New Zealand</option>
                        <option value="Nigeria">Nigeria</option>
                        <option value="Norway">Norway</option>
                        <option value="Oman">Oman</option>
                        <option value="Pakistan">Pakistan</option>
                        <option value="Philippines">Philippines</option>
                        <option value="Poland">Poland</option>
                        <option value="Portugal">Portugal</option>
                        <option value="Qatar">Qatar</option>
                        <option value="Russia">Russia</option>
                        <option value="Saudi Arabia">Saudi Arabia</option>
                        <option value="Singapore">Singapore</option>
                        <option value="South Africa">South Africa</option>
                        <option value="South Korea">South Korea</option>
                        <option value="Spain">Spain</option>
                        <option value="Sri Lanka">Sri Lanka</option>
                        <option value="Sweden">Sweden</option>
                        <option value="Switzerland">Switzerland</option>
                        <option value="Thailand">Thailand</option>
                        <option value="Turkey">Turkey</option>
                        <option value="Ukraine">Ukraine</option>
                        <option value="United Arab Emirates">United Arab Emirates</option>
                        <option value="United Kingdom">United Kingdom</option>
                        <option value="United States">United States</option>
                        <option value="Vietnam">Vietnam</option>
                        <option value="Yemen">Yemen</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Service Mode Section */}
            <div className="form-section-container">
              <div className="form-section-header" onClick={() => setIsServiceModeOpen(!isServiceModeOpen)} role="button" tabIndex={0}>
                <div className="header-content">
                  <FileText size={20} />
                  <h2 className="section-title">Service Mode</h2>
                </div>
                {isServiceModeOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </div>
              {isServiceModeOpen && (
                <div className="form-section-body">
                  <div>
                    <label className="form-field-label">
                      Mode <span className="required-star">*</span>
                    </label>
                    <div className="service-mode-options">
                      {["VISIT", "COURIER", "SOMEONE", "VOICE", "VIDEO"].map((option) => (
                        <label key={option} className="service-mode-option">
                          <input
                            type="radio"
                            name="service_type"
                            value={option}
                            checked={formData.service_type === option}
                            onChange={handleChange}
                          />
                          <span>{option}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Submit Button */}
            <div className="submit-button-container">
              <button className="submit-button" type="submit">Update Patient</button>
            </div>
          </form>
        </main>
      </div>
    </div>
  );
};

export default EditPatient;
